import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useCases, industries } from "../data/mock";
import {
  Activity,
  ClipboardCheck,
  Radio,
  GitBranch,
  Database,
  Workflow,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { Reveal } from "../components/animations/Reveal";
import { DataFlowMini } from "../components/animations/DataFlowAnimation";
import { cn } from "@/lib/utils";

const iconMap = { Activity, ClipboardCheck, Radio, GitBranch, Database, Workflow };

const accentText = {
  igaming: "text-[hsl(var(--igaming))]",
  "battery-tech": "text-[hsl(var(--battery))]",
  automotive: "text-[hsl(var(--automotive))]",
};
const accentBg = {
  igaming: "bg-[hsl(var(--igaming))]/10",
  "battery-tech": "bg-[hsl(var(--battery))]/10",
  automotive: "bg-[hsl(var(--automotive))]/10",
};

const industryLookup = Object.fromEntries(industries.map((i) => [i.slug, i]));

export default function UseCases() {
  return (
    <div className="bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-28 border-b border-border">
        <div className="absolute inset-0 graph-bg" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-primary text-sm font-mono uppercase tracking-wider mb-4">Use Cases</p>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground leading-tight mb-6">
              Illustrative Pipelines, By Industry
            </h1>
            <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
              Concrete examples of how IaC, container orchestration, security, and workflow
              automation come together for SCADA, ADAS/AV, and iGaming data.
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases Grid */}
      <section className="py-12 lg:py-20 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-8">
            {useCases.map((useCase, index) => {
              const Icon = iconMap[useCase.icon] || Activity;
              const ind = industryLookup[useCase.industry];

              return (
                <Reveal key={useCase.id} delay={(index % 3) * 80}>
                  <Card className="bg-card border-border hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 overflow-hidden group">
                    <CardContent className="p-0">
                      <div className="flex flex-col lg:flex-row">
                        {/* Left: Icon and Number */}
                        <div className="lg:w-32 p-6 lg:p-8 bg-secondary/40 flex flex-row lg:flex-col items-center lg:items-start gap-4 lg:gap-6">
                          <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center", accentBg[useCase.industry])}>
                            <Icon className={cn("w-7 h-7", accentText[useCase.industry])} />
                          </div>
                          <span className="text-4xl font-bold text-border font-mono">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                        </div>

                        {/* Right: Content */}
                        <div className="flex-1 p-6 lg:p-8">
                          <div className="flex items-center gap-2 mb-3 flex-wrap">
                            <h3 className="text-2xl font-semibold text-foreground">
                              {useCase.title}
                            </h3>
                            {ind && (
                              <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium", accentBg[useCase.industry], accentText[useCase.industry])}>
                                {ind.name}
                              </span>
                            )}
                          </div>
                          <p className="text-muted-foreground leading-relaxed mb-6">
                            {useCase.description}
                          </p>
                          <div className="flex items-center gap-3 p-4 rounded-xl bg-secondary/60">
                            <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                            <span className="text-secondary-foreground font-medium">
                              Outcome: <span className="text-primary">{useCase.outcome}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Data Flow Preview */}
      <section className="py-16 lg:py-20 bg-card/40 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center mb-8">
            <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
              The Same Pipeline Shape, Every Time
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Ingest, secure, orchestrate, automate, analyze — the pattern repeats across every use case above
            </p>
          </Reveal>
          <DataFlowMini />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="relative overflow-hidden rounded-3xl bg-card border border-border p-12 lg:p-16 text-center">
            <div className="absolute inset-0 graph-bg-dense opacity-50" />
            <div className="relative">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Have a Similar Challenge?
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
                Tell us what your data pipeline looks like today — we'll map it to the capabilities that matter.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8 h-12">
                  Talk to an Architect
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
